// Часть разбиения MapView.vue (фаза 2) - режим поворота карты (north-up /
// compass / manual) и переключатель 3D-рельефа.
//
// Обе фичи слегка завязаны на GPS (useGpsTracking.ts): cycleRotationMode
// запрашивает разрешение на компас и переигрывает последний известный
// heading. Обратной зависимости при этом нет (GPS лишь ЧИТАЕТ
// mapRotationMode как обычный Ref, передаваемый параметром), так что цикл
// разрывается опциями-колбэками, а не слиянием composable'ов - см. вызов в
// MapView.vue (forward-ref через gpsRef, тот же приём, что и у
// useRouteCursor/useRouteDrawing).
import { computed, ref } from "vue";
import { TERRAIN_EXAGGERATION, TERRAIN_PITCH_DEG, TERRAIN_SOURCE } from "../map/constants";
import type { MapCtx } from "../map/mapContext";

// 'north-up'  bearing зафиксирован на 0, конус направления поворачивается на
//             месте (поведение из до-MapLibre версии, сохранено как было).
// 'compass'   карта сама поворачивается так, что направление устройства
//             всегда сверху (как в навигаторе), маркер показывает
//             фиксированную стрелку вперёд.
// 'manual'    пользователь сам покрутил карту - больше не подстраивать
//             bearing под живой компас, пока не попросит вернуть.
export type MapRotationMode = "north-up" | "compass" | "manual";

export interface UseMapRotation3DOptions {
  attachHeadingListener: () => void;
  getLastHeadingDeg: () => number | null;
  setUserHeading: (heading: number | null) => void;
}

export function useMapRotation3D(ctx: MapCtx, options: UseMapRotation3DOptions) {
  const mapRotationMode = ref<MapRotationMode>("north-up");
  const is3D = ref(false);
  // Выставляется вокруг собственных easeTo-вызовов от компаса. MapLibre шлёт
  // rotatestart на ЛЮБОЕ изменение bearing, включая программное, поэтому без
  // этого флага (и соседней проверки originalEvent в handleRotateStart)
  // компас тут же сам сбил бы себя обратно из режима compass на первом же
  // своём повороте.
  let programmaticRotate = false;

  // Вызывается и отсюда (переход в north-up), и из useGpsTracking.ts
  // (setUserHeading перед своим easeTo к живому heading) - оба места гонят
  // bearing программно и должны на мгновение подавить map.on("rotatestart")
  // (handleRotateStart ниже), иначе он тут же спутал бы это с ручным
  // поворотом и сбросил режим в 'manual'.
  const markProgrammaticRotate = () => {
    programmaticRotate = true;
    requestAnimationFrame(() => {
      programmaticRotate = false;
    });
  };

  const rotationButtonLabel = computed(() =>
    mapRotationMode.value === "compass" ? ctx.t("map.northUp") : ctx.t("map.compassMode")
  );

  const cycleRotationMode = () => {
    const map = ctx.getMap();
    if (!map) return;
    if (mapRotationMode.value === "compass") {
      mapRotationMode.value = "north-up";
    } else {
      // Из 'manual' тоже попадает сначала на north-up, а не сразу в
      // compass-слежение: пользователь намеренно взял управление bearing,
      // первое нажатие должно просто это отменить.
      mapRotationMode.value = mapRotationMode.value === "manual" ? "north-up" : "compass";
    }
    if (mapRotationMode.value === "north-up") {
      markProgrammaticRotate();
      map.easeTo({ bearing: 0, duration: 300 });
    } else {
      const lastHeadingDeg = options.getLastHeadingDeg();
      if (lastHeadingDeg != null) options.setUserHeading(lastHeadingDeg);
    }
    // Режиму compass нужен heading устройства, на iOS его можно запросить
    // только изнутри пользовательского жеста - этот клик им и является.
    if (mapRotationMode.value === "compass") options.attachHeadingListener();
  };

  const toggle3D = () => {
    const map = ctx.getMap();
    if (!map) return;
    is3D.value = !is3D.value;
    if (is3D.value) {
      map.setTerrain({ source: TERRAIN_SOURCE, exaggeration: TERRAIN_EXAGGERATION });
      map.easeTo({ pitch: TERRAIN_PITCH_DEG, duration: 600 });
    } else {
      map.setTerrain(null);
      map.easeTo({ pitch: 0, duration: 600 });
    }
  };

  // Регистрируется в setupMapLayers (MapView.vue) как map.on("rotatestart", ...) -
  // срабатывает и на программные изменения bearing - только у жеста есть
  // originalEvent, programmaticRotate покрывает собственные вызовы easeTo.
  const handleRotateStart = (e: any) => {
    if (!e?.originalEvent || programmaticRotate) return;
    if (mapRotationMode.value !== "manual") mapRotationMode.value = "manual";
  };

  return {
    mapRotationMode,
    is3D,
    rotationButtonLabel,
    cycleRotationMode,
    toggle3D,
    handleRotateStart,
    markProgrammaticRotate,
  };
}
